import React, { useState, useEffect, useMemo, useCallback } from 'react'
import classNames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Modal, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'

import { IMainState } from 'store/states'
import avatarDefault from 'assets/avatar-loading-xxl.svg'
import Button from 'design-system/button'
import Avatar from 'design-system/avatar'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import SelectInput from 'components/ui/forms/field/select-input/SelectInput'
import * as fromUserActions from 'store/actions/user.action'
import { IUserModel } from 'models/user.model'
import styles from './EditSupervisorsModal.module.scss'

interface IEditSupervisorsModalModal {
  show: boolean
  onClose: any
  userId: number
  initialState: IUserModel[]
}

interface Option {
  value: string
  label: string
  avatar?: string | null
  position?: string
}

const EditSupervisorsModal: React.FC<IEditSupervisorsModalModal> = ({
  show,
  onClose,
  initialState,
  userId,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [editLoading, setEditLoading] = useState(false)

  const dispatchSearch = (searchText) => {
    dispatch(fromUserActions.Actions.search({ searchText }))
  }

  const { data, loading } = useSelector(({ user }: IMainState) => user.list)

  const mapOption = useCallback(
    (user: IUserModel): Option => ({
      value: `${user.id || 0}`,
      label: [user.name, user.lastname].join(' '),
      avatar: user.avatar,
      position: user.position,
    }),
    []
  )

  const options = useMemo(
    () => data.filter((user) => `${user.id}` !== `${userId}`).map(mapOption),
    [data, mapOption, userId]
  )

  const [selected, setSelected] = useState<Option[]>([])

  useEffect(() => {
    if (initialState)
      setSelected(
        initialState
          .map(mapOption)
          .filter(({ value }) => `${value}` !== `${userId}`)
      )
  }, [initialState, mapOption, userId])

  const handleSubmit = () => {
    const supervisors = selected ? selected.map(({ value }) => value) : []
    setEditLoading(true)
    dispatch(
      fromUserActions.Actions.updateUserSupervisors({
        userId,
        supervisors,
        onSuccess: () => {
          onClose()
          setEditLoading(false)
        },
        onError: () => {
          setEditLoading(false)
        },
      })
    )
  }

  return (
    <Modal show={show} onHide={onClose} className={styles.modalContainer}>
      <Modal.Body className={'pt-0'}>
        <Row className="position-relative">
          <h1>{t('configuration.user.modalSupervisors.title')}</h1>
          <a className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </a>
        </Row>
        <Row className="d-block mt-3">
          <SelectInput
            name="optionalProfiles"
            label={t('configuration.user.modalSupervisors.supervisors')}
            closeMenuOnSelect={true}
            options={options}
            value={selected}
            isMulti={true}
            onChange={(selected) => {
              setSelected([...(selected || [])])
            }}
            cleareable={false}
            error={''}
            onInputChange={(text) => {
              dispatchSearch(text)
            }}
            isLoading={loading}
            components={{
              Option: ({ data, innerProps, className }) => (
                <div
                  {...innerProps}
                  className={classNames(className, styles.option)}
                >
                  <Avatar
                    src={data.avatar || avatarDefault}
                    description={{
                      primaryText: data.label,
                      secundaryText: data.position,
                    }}
                  />
                </div>
              ),
            }}
          />
        </Row>
        <Row className={styles.footer}>
          <Button
            variant={ButtonVariant.Secondary}
            size={ButtonSizes.Small}
            onClick={onClose}
          >
            {t('configuration.user.modalSupervisors.buttons.cancel')}
          </Button>
          <Button
            className="ml-2 mr-3 "
            variant={ButtonVariant.Primary}
            size={ButtonSizes.Small}
            disabled={editLoading}
            onClick={handleSubmit}
          >
            {t('configuration.user.modalSupervisors.buttons.save')}
          </Button>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default EditSupervisorsModal
